import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import { Category, ProductImage, ProductAttributes, AttributePlacement } from '../../types';
import { adminAPI } from '../../services/api';
import { getImageUrl } from '../../utils/imageUrl';
import { normalizeOptions } from '../../utils/attributeHelpers';
import RichTextEditor from '../common/RichTextEditor';
import { DollarSign, AlertTriangle, Package, Truck, Info, Image as ImageIcon, FileText, CheckCircle2, Sliders, Tag } from 'lucide-react';
import './AdminFormStyles.css';
import './ProductFormWorkspace.css';

interface ProductFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: any) => Promise<any>; // Returns created/updated product with ID
  product?: any | null;
  categories: Category[];
}

const ProductFormModal: React.FC<ProductFormModalProps> = ({
  isOpen,
  onClose,
  onSave,
  product,
  categories
}) => {
  const [descLang, setDescLang] = useState<'bn' | 'en'>('bn');
  const [formData, setFormData] = useState({
    name_bn: '',
    name_en: '',
    slug: '',
    category: '',
    summary_bn: '',
    summary_en: '',
    description_bn: '',
    description_en: '',
    price: '',
    discount_price: '',
    buying_price: '',
    stock: '',
    low_stock_threshold: '5',
    sku: '',
    barcode: '',
    supplier_name: '',
    supplier_contact: '',
    supplier_memo_no: '',
    weight_kg: '',
    image: null as File | null,
    is_active: true,
    is_featured: false,
    free_delivery: false
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<any>({});
  
  // Multi-image state
  const [existingImages, setExistingImages] = useState<ProductImage[]>([]);
  const [additionalImages, setAdditionalImages] = useState<File[]>([]);
  const [imageUploading, setImageUploading] = useState(false);
  
  // Attributes state
  interface OptionInputState {
    name: string;
    extra_price: string;
  }

  const defaultAttributes: ProductAttributes = {
    colors:  { enabled: false, placement: 'default', options: [] },
    sizes:   { enabled: false, placement: 'default', options: [] },
    weights: { enabled: false, placement: 'default', options: [] },
    custom:  [],
  };
  const [attributes, setAttributes] = useState<ProductAttributes>(defaultAttributes);
  const [attrInputs, setAttrInputs] = useState<{
    colors: OptionInputState;
    sizes: OptionInputState;
    weights: OptionInputState;
  }>({
    colors:  { name: '', extra_price: '' },
    sizes:   { name: '', extra_price: '' },
    weights: { name: '', extra_price: '' },
  });
  const [customAttrInputs, setCustomAttrInputs] = useState<Record<string, OptionInputState>>({});
  const [newGroupName, setNewGroupName] = useState('');
  const [showNewGroupInput, setShowNewGroupInput] = useState(false);

  // Add key for forcing RichTextEditor re-render
  const [editorKey, setEditorKey] = useState(0);

  useEffect(() => {
    if (product) {
      console.log('Editing product:', product);
      console.log('Description BN length:', product.description_bn?.length || 0);
      console.log('Description EN length:', product.description_en?.length || 0);
      console.log('Product images:', product.images);
      console.log('Number of images:', product.images?.length || 0);
      
      const newFormData = {
        name_bn: product.name_bn || '',
        name_en: product.name_en || '',
        slug: product.slug || '',
        category: product.category?.id || product.category || '',
        summary_bn: product.summary_bn || '',
        summary_en: product.summary_en || '',
        description_bn: product.description_bn || '',
        description_en: product.description_en || '',
        price: product.price || '',
        discount_price: product.discount_price || '',
        buying_price: product.buying_price !== undefined && product.buying_price !== null ? String(product.buying_price) : '',
        stock: product.stock !== undefined && product.stock !== null ? String(product.stock) : '',
        low_stock_threshold: product.low_stock_threshold !== undefined && product.low_stock_threshold !== null ? String(product.low_stock_threshold) : '5',
        sku: product.sku || '',
        barcode: product.barcode || '',
        supplier_name: product.supplier_name || '',
        supplier_contact: product.supplier_contact || '',
        supplier_memo_no: product.supplier_memo_no || '',
        weight_kg: product.weight_kg !== undefined && product.weight_kg !== null ? String(product.weight_kg) : '',
        image: null,
        is_active: product.is_active ?? true,
        is_featured: product.is_featured ?? false,
        free_delivery: product.free_delivery ?? false
      };
      
      console.log('Setting formData with descriptions:', {
        bn: newFormData.description_bn.substring(0, 100),
        en: newFormData.description_en.substring(0, 100)
      });
      
      setFormData(newFormData);
      // Load existing images
      const images = product.images || [];
      console.log('Setting existingImages:', images);
      setExistingImages(images);
      // Load existing attributes
      const rawAttrs = product.attributes || {};
      setAttributes({
        colors:  rawAttrs.colors  ? { enabled: !!rawAttrs.colors.enabled, placement: rawAttrs.colors.placement || 'default', options: normalizeOptions(rawAttrs.colors.options) } : { enabled: false, placement: 'default', options: [] },
        sizes:   rawAttrs.sizes   ? { enabled: !!rawAttrs.sizes.enabled, placement: rawAttrs.sizes.placement || 'default', options: normalizeOptions(rawAttrs.sizes.options) } : { enabled: false, placement: 'default', options: [] },
        weights: rawAttrs.weights ? { enabled: !!rawAttrs.weights.enabled, placement: rawAttrs.weights.placement || 'default', options: normalizeOptions(rawAttrs.weights.options) } : { enabled: false, placement: 'default', options: [] },
        custom:  Array.isArray(rawAttrs.custom) ? rawAttrs.custom.map((cg: any) => ({
          id: cg.id || cg.name,
          name: cg.name || '',
          name_bn: cg.name_bn || cg.name || '',
          name_en: cg.name_en || cg.name || '',
          enabled: cg.enabled ?? true,
          placement: cg.placement || 'default',
          options: normalizeOptions(cg.options),
        })) : [],
      });
    } else {
      setFormData({
        name_bn: '',
        name_en: '',
        slug: '',
        category: '',
        summary_bn: '',
        summary_en: '',
        description_bn: '',
        description_en: '',
        price: '',
        discount_price: '',
        buying_price: '',
        stock: '',
        low_stock_threshold: '5',
        sku: '',
        barcode: '',
        supplier_name: '',
        supplier_contact: '',
        supplier_memo_no: '',
        weight_kg: '',
        image: null,
        is_active: true,
        is_featured: false,
        free_delivery: false
      });
      setExistingImages([]);
      setAttributes({
        colors: { enabled: false, options: [] },
        sizes: { enabled: false, options: [] },
        weights: { enabled: false, options: [] },
        custom: []
      });
    }
    setAdditionalImages([]);
    setAttrInputs({
      colors: { name: '', extra_price: '' },
      sizes: { name: '', extra_price: '' },
      weights: { name: '', extra_price: '' }
    });
    setCustomAttrInputs({});
    setNewGroupName('');
    setShowNewGroupInput(false);
    setErrors({});
    // Increment key synchronously to force RichTextEditor remount
    setEditorKey(prev => prev + 1);
  }, [product, isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;

    if (type === 'file') {
      const files = (e.target as HTMLInputElement).files;
      if (files && files.length > 0) {
        setFormData(prev => ({
          ...prev,
          image: files[0]
        }));
      }
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (errors[name]) {
      setErrors((prev: any) => ({ ...prev, [name]: null }));
    }
  };

  const handleRichTextChange = (name: string, value: string) => {
    // Guard: don't let Quill's initialization onChange overwrite real content with empty
    const isQuillEmpty = (html: string) => {
      if (!html) return true;
      return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, '').trim() === '';
    };

    setFormData(prev => {
      const current = prev[name as keyof typeof prev];
      // If Quill fires onChange with empty content but formData already has real content, skip
      if (typeof current === 'string' && current.length > 15 && isQuillEmpty(value)) {
        return prev;
      }
      return { ...prev, [name]: value };
    });

    if (errors[name]) {
      setErrors((prev: any) => ({ ...prev, [name]: null }));
    }
  };

  // Helper function to check if HTML content is empty
  const isHtmlEmpty = (html: string): boolean => {
    if (!html || html.trim() === '') return true;
    
    // Remove HTML tags and check if there's actual content
    const text = html.replace(/<[^>]*>/g, '').trim();
    return text === '';
  };

  const handleAdditionalImagesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      setAdditionalImages(prev => [...prev, ...Array.from(files)]);
    }
  };

  const removeAdditionalImage = (index: number) => {
    setAdditionalImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleDeleteExistingImage = async (imageId: number) => {
    if (!product?.id) return;
    
    try {
      await adminAPI.deleteProductImage(product.id, imageId);
      setExistingImages(prev => prev.filter(img => img.id !== imageId));
    } catch (error) {
      console.error('Failed to delete image:', error);
    }
  };

  const generateSlug = () => {
    const slug = formData.name_en
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
    setFormData(prev => ({ ...prev, slug }));
  };

  // ── Attribute helpers ─────────────────────────────────────────────
  type AttrKey = 'colors' | 'sizes' | 'weights';

  const toggleAttr = (key: AttrKey) => {
    setAttributes(prev => ({
      ...prev,
      [key]: { ...prev[key]!, enabled: !prev[key]!.enabled },
    }));
  };

  const addAttrOption = (key: AttrKey) => {
    const input = attrInputs[key];
    const val = input.name.trim();
    if (!val) return;
    const extraPrice = parseFloat(input.extra_price) || 0;

    setAttributes(prev => {
      const existing = normalizeOptions(prev[key]?.options);
      if (existing.some(o => o.name.toLowerCase() === val.toLowerCase())) return prev;
      return {
        ...prev,
        [key]: {
          ...prev[key]!,
          options: [...existing, { name: val, extra_price: extraPrice }]
        }
      };
    });
    setAttrInputs(prev => ({ ...prev, [key]: { name: '', extra_price: '' } }));
  };

  const removeAttrOption = (key: AttrKey, optionName: string) => {
    setAttributes(prev => ({
      ...prev,
      [key]: {
        ...prev[key]!,
        options: normalizeOptions(prev[key]?.options).filter(o => o.name !== optionName)
      }
    }));
  };

  const handleAttrKeyDown = (key: AttrKey, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') { e.preventDefault(); addAttrOption(key); }
  };

  // Custom Attribute Groups helpers
  const addCustomGroup = () => {
    const name = newGroupName.trim();
    if (!name) return;
    const id = 'custom_' + Date.now();
    setAttributes(prev => ({
      ...prev,
      custom: [
        ...(prev.custom || []),
        { id, name, name_bn: name, name_en: name, enabled: true, placement: 'default', options: [] }
      ]
    }));
    setNewGroupName('');
    setShowNewGroupInput(false);
  };

  const removeCustomGroup = (groupId: string) => {
    setAttributes(prev => ({
      ...prev,
      custom: (prev.custom || []).filter(g => g.id !== groupId)
    }));
  };

  const toggleCustomGroup = (groupId: string) => {
    setAttributes(prev => ({
      ...prev,
      custom: (prev.custom || []).map(g => g.id === groupId ? { ...g, enabled: !g.enabled } : g)
    }));
  };

  const addCustomOption = (groupId: string) => {
    const input = customAttrInputs[groupId] || { name: '', extra_price: '' };
    const val = input.name.trim();
    if (!val) return;
    const extraPrice = parseFloat(input.extra_price) || 0;

    setAttributes(prev => ({
      ...prev,
      custom: (prev.custom || []).map(g => {
        if (g.id !== groupId) return g;
        const existing = normalizeOptions(g.options);
        if (existing.some(o => o.name.toLowerCase() === val.toLowerCase())) return g;
        return {
          ...g,
          options: [...existing, { name: val, extra_price: extraPrice }]
        };
      })
    }));

    setCustomAttrInputs(prev => ({ ...prev, [groupId]: { name: '', extra_price: '' } }));
  };

  const removeCustomOption = (groupId: string, optionName: string) => {
    setAttributes(prev => ({
      ...prev,
      custom: (prev.custom || []).map(g => {
        if (g.id !== groupId) return g;
        return {
          ...g,
          options: normalizeOptions(g.options).filter(o => o.name !== optionName)
        };
      })
    }));
  };

  const updateCustomGroupPlacement = (groupId: string, placement: AttributePlacement) => {
    setAttributes(prev => ({
      ...prev,
      custom: (prev.custom || []).map(g => (g.id === groupId || g.name === groupId) ? { ...g, placement } : g)
    }));
  };

  const updateAttrPlacement = (key: AttrKey, placement: AttributePlacement) => {
    setAttributes(prev => ({
      ...prev,
      [key]: { ...prev[key]!, placement }
    }));
  };
  // ─────────────────────────────────────────────────────────────────

  const validate = () => {
    const newErrors: any = {};

    if (!formData.name_bn.trim()) newErrors.name_bn = 'Bangla name is required';
    if (!formData.name_en.trim()) newErrors.name_en = 'English name is required';
    if (!formData.slug.trim()) newErrors.slug = 'Slug is required';
    if (!formData.category) newErrors.category = 'Category is required';
    
    // Validate rich text content
    if (isHtmlEmpty(formData.description_bn)) {
      newErrors.description_bn = 'Bangla description is required';
    }
    if (isHtmlEmpty(formData.description_en)) {
      newErrors.description_en = 'English description is required';
    }
    
    if (!formData.price || parseFloat(formData.price) <= 0) newErrors.price = 'Price must be greater than 0';
    if (formData.discount_price && parseFloat(formData.discount_price) >= parseFloat(formData.price)) {
      newErrors.discount_price = 'Discount price must be less than regular price';
    }
    if (formData.buying_price && parseFloat(formData.buying_price) < 0) {
      newErrors.buying_price = 'Buying price cannot be negative';
    }
    if (!formData.stock || parseInt(formData.stock) < 0) newErrors.stock = 'Stock must be 0 or greater';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    setLoading(true);
    try {
      const data = new FormData();
      data.append('name_bn', formData.name_bn);
      data.append('name_en', formData.name_en);
      data.append('slug', formData.slug);
      data.append('category', formData.category);
      
      // Clean up HTML content - if empty, send empty string
      const cleanSummaryBn = isHtmlEmpty(formData.summary_bn) ? '' : formData.summary_bn;
      const cleanSummaryEn = isHtmlEmpty(formData.summary_en) ? '' : formData.summary_en;
      const cleanDescriptionBn = isHtmlEmpty(formData.description_bn) ? '' : formData.description_bn;
      const cleanDescriptionEn = isHtmlEmpty(formData.description_en) ? '' : formData.description_en;
      
      data.append('summary_bn', cleanSummaryBn);
      data.append('summary_en', cleanSummaryEn);
      data.append('description_bn', cleanDescriptionBn);
      data.append('description_en', cleanDescriptionEn);
      data.append('price', formData.price);
      if (formData.discount_price) data.append('discount_price', formData.discount_price);
      data.append('buying_price', formData.buying_price || '0');
      data.append('stock', formData.stock);
      data.append('low_stock_threshold', formData.low_stock_threshold || '5');
      data.append('sku', formData.sku || '');
      data.append('barcode', formData.barcode || '');
      data.append('supplier_name', formData.supplier_name || '');
      data.append('supplier_contact', formData.supplier_contact || '');
      data.append('supplier_memo_no', formData.supplier_memo_no || '');
      if (formData.weight_kg) data.append('weight_kg', formData.weight_kg);
      data.append('is_active', formData.is_active.toString());
      data.append('is_featured', formData.is_featured.toString());
      data.append('free_delivery', formData.free_delivery.toString());

      // Append attributes as JSON string (parsed by backend)
      data.append('attributes', JSON.stringify(attributes));

      if (formData.image instanceof File) {
        data.append('image', formData.image);
      }

      // onSave returns the created/updated product
      const savedProduct = await onSave(data);
      
      // Upload additional images if any
      // Use product.id for existing products, or savedProduct.id for newly created
      const productId = product?.id || savedProduct?.id;
      if (additionalImages.length > 0 && productId) {
        setImageUploading(true);
        try {
          await adminAPI.uploadProductImages(productId, additionalImages);
        } catch (imgError) {
          console.error('Failed to upload additional images:', imgError);
        }
        setImageUploading(false);
      }
      
      onClose();
    } catch (error: any) {
      console.error('Form submission error:', error);
      if (error.response?.data) {
        setErrors(error.response.data);
      }
    } finally {
      setLoading(false);
    }
  };

  // Real-time Profit & Margin Calculations
  const regularPriceNum = parseFloat(formData.price) || 0;
  const discountPriceNum = parseFloat(formData.discount_price) || 0;
  const buyingPriceNum = parseFloat(formData.buying_price) || 0;
  const effectiveSellingPrice = discountPriceNum > 0 ? discountPriceNum : regularPriceNum;
  const unitProfit = effectiveSellingPrice > 0 ? effectiveSellingPrice - buyingPriceNum : 0;
  const profitMargin = effectiveSellingPrice > 0 ? Math.round(((effectiveSellingPrice - buyingPriceNum) / effectiveSellingPrice) * 100 * 10) / 10 : 0;
  const markupPercentage = buyingPriceNum > 0 ? Math.round(((effectiveSellingPrice - buyingPriceNum) / buyingPriceNum) * 100 * 10) / 10 : 0;
  const isLoss = effectiveSellingPrice > 0 && buyingPriceNum > 0 && effectiveSellingPrice <= buyingPriceNum;
  const hasPricingData = regularPriceNum > 0 && buyingPriceNum > 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={product ? `Edit Product: ${product.name_en || product.name_bn || ''}` : 'Add New Product'}
      size="xlarge"
      className="pf-modal-window"
    >
      <form onSubmit={handleSubmit} className="pf-workspace">
        <div className="pf-grid">
          {/* ========================================================
              LEFT COLUMN (65% width) - Main Content
             ======================================================== */}
          <div className="pf-col-main">
            {/* Card 1: Basic Product Information */}
            <div className="pf-card">
              <div className="pf-card-header">
                <h3 className="pf-card-title">
                  <Info size={17} /> Basic Product Information (পণ্যের নাম ও পরিচিতি)
                </h3>
                <span className="pf-card-subtitle">Required identity</span>
              </div>
              <div className="pf-card-body">
                <div className="pf-row-2">
                  <div className="pf-field">
                    <label className="pf-label">
                      Name (Bangla) <span className="req">*</span>
                    </label>
                    <input
                      type="text"
                      name="name_bn"
                      value={formData.name_bn}
                      onChange={handleChange}
                      placeholder="পণ্যের নাম বাংলায় লিখুন"
                      className={`pf-input ${errors.name_bn ? 'has-error' : ''}`}
                    />
                    {errors.name_bn && <span className="pf-error">{errors.name_bn}</span>}
                  </div>
                  <div className="pf-field">
                    <label className="pf-label">
                      Name (English) <span className="req">*</span>
                    </label>
                    <input
                      type="text"
                      name="name_en"
                      value={formData.name_en}
                      onChange={handleChange}
                      onBlur={generateSlug}
                      placeholder="Enter English product name"
                      className={`pf-input ${errors.name_en ? 'has-error' : ''}`}
                    />
                    {errors.name_en && <span className="pf-error">{errors.name_en}</span>}
                  </div>
                </div>

                <div className="pf-field">
                  <label className="pf-label">
                    <span>URL Slug <span className="req">*</span></span>
                    <span className="pf-label-badge">Auto-generates from English name</span>
                  </label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.85rem', color: '#94a3b8', fontWeight: 500 }}>/products/</span>
                    <input
                      type="text"
                      name="slug"
                      value={formData.slug}
                      onChange={handleChange}
                      placeholder="product-url-slug"
                      className={`pf-input ${errors.slug ? 'has-error' : ''}`}
                      style={{ flex: 1 }}
                    />
                    <button
                      type="button"
                      onClick={generateSlug}
                      className="pf-btn-cancel"
                      style={{ padding: '7px 12px', fontSize: '0.78rem', whiteSpace: 'nowrap' }}
                      title="Generate slug from English name"
                    >
                      Regenerate
                    </button>
                  </div>
                  {errors.slug && <span className="pf-error">{errors.slug}</span>}
                </div>
              </div>
            </div>

            {/* Card 2: Media & Gallery */}
            <div className="pf-card">
              <div className="pf-card-header">
                <h3 className="pf-card-title">
                  <ImageIcon size={17} /> Product Media & Images (ছবি ও গ্যালারি)
                </h3>
                <span className="pf-card-subtitle">Main image and gallery photos</span>
              </div>
              <div className="pf-card-body">
                <div className="pf-media-grid">
                  {/* Main Image */}
                  <div className="pf-field">
                    <label className="pf-label">Main Image</label>
                    <label className="pf-main-img-box" title="Click to choose main image">
                      <input
                        type="file"
                        name="image"
                        onChange={handleChange}
                        accept="image/*"
                        style={{ display: 'none' }}
                      />
                      {formData.image ? (
                        <img
                          src={URL.createObjectURL(formData.image)}
                          alt="Preview"
                          className="pf-main-img-preview"
                        />
                      ) : product?.image ? (
                        <img
                          src={getImageUrl(product.image)}
                          alt="Current"
                          className="pf-main-img-preview"
                        />
                      ) : (
                        <div style={{ textAlign: 'center', padding: '10px' }}>
                          <ImageIcon size={28} color="#94a3b8" />
                          <span style={{ fontSize: '0.72rem', color: '#64748b', display: 'block', marginTop: '4px' }}>
                            Upload Main
                          </span>
                        </div>
                      )}
                    </label>
                    <span className="pf-hint">
                      {formData.image ? `New: ${formData.image.name}` : product?.image ? 'Current photo' : 'Square photo'}
                    </span>
                  </div>

                  {/* Gallery Images */}
                  <div className="pf-field pf-gallery-container">
                    <label className="pf-label">
                      <span>Gallery Images</span>
                      <span className="pf-label-badge">Multiple allowed</span>
                    </label>

                    {/* Existing gallery thumbnails */}
                    {product?.id && existingImages.length > 0 && (
                      <div className="pf-gallery-previews">
                        {existingImages.map((img) => (
                          <div key={img.id} className={`pf-gallery-thumb ${img.is_primary ? 'is-primary' : ''}`}>
                            <img src={getImageUrl(img.image)} alt="Gallery" />
                            {img.is_primary && <span className="pf-thumb-badge">★ Main</span>}
                            <button
                              type="button"
                              className="pf-thumb-remove-btn"
                              onClick={() => handleDeleteExistingImage(img.id)}
                              title="Delete this image"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Staged new additional images */}
                    {additionalImages.length > 0 && (
                      <div className="pf-gallery-previews" style={{ background: '#fef3c7', padding: '8px', borderRadius: '8px', border: '1px dashed #d97706' }}>
                        {additionalImages.map((file, idx) => (
                          <div key={idx} className="pf-gallery-thumb" style={{ borderColor: '#d97706' }}>
                            <img src={URL.createObjectURL(file)} alt="Staged" />
                            <button
                              type="button"
                              className="pf-thumb-remove-btn"
                              onClick={() => removeAdditionalImage(idx)}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="pf-file-dropzone">
                      <input
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={handleAdditionalImagesChange}
                      />
                      <span style={{ fontSize: '0.74rem', color: '#64748b' }}>Select extra photos</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Card 3: Descriptions & Summaries with Language Switcher */}
            <div className="pf-card">
              <div className="pf-card-header">
                <h3 className="pf-card-title">
                  <FileText size={17} /> Product Descriptions (পণ্যের বিবরণ ও সারসংক্ষেপ)
                </h3>
                <div className="pf-lang-tabs">
                  <button
                    type="button"
                    className={`pf-lang-tab ${descLang === 'bn' ? 'active' : ''}`}
                    onClick={() => setDescLang('bn')}
                  >
                    <span>🇧🇩 বাংলা</span>
                  </button>
                  <button
                    type="button"
                    className={`pf-lang-tab ${descLang === 'en' ? 'active' : ''}`}
                    onClick={() => setDescLang('en')}
                  >
                    <span>🇬🇧 English</span>
                  </button>
                </div>
              </div>
              <div className="pf-card-body">
                {/* Bangla Content Panel */}
                <div style={{ display: descLang === 'bn' ? 'flex' : 'none', flexDirection: 'column', gap: '16px' }}>
                  <div className="pf-field">
                    <label className="pf-label">
                      <span>Summary (বাংলা সারসংক্ষেপ)</span>
                      <span className="pf-label-badge">Short Overview</span>
                    </label>
                    <RichTextEditor
                      key={`summary-bn-${product?.id || 'new'}-${editorKey}`}
                      value={formData.summary_bn || product?.summary_bn || ''}
                      onChange={(value) => handleRichTextChange('summary_bn', value)}
                      placeholder="পণ্যের সংক্ষিপ্ত বিবরণ..."
                      minHeight="120px"
                    />
                    <span className="pf-hint">প্রোডাক্টের মূল আকর্ষণ ও সংক্ষিপ্ত পয়েন্টসমূহ</span>
                  </div>

                  <div className="pf-field">
                    <label className="pf-label">
                      <span>Detailed Description (বিস্তারিত বাংলা বর্ণনা) <span className="req">*</span></span>
                    </label>
                    <RichTextEditor
                      key={`description-bn-${product?.id || 'new'}-${editorKey}`}
                      value={formData.description_bn || product?.description_bn || ''}
                      onChange={(value) => handleRichTextChange('description_bn', value)}
                      placeholder="পণ্যের বিস্তারিত বর্ণনা ও বৈশিষ্ট্য লিখুন..."
                      className={errors.description_bn ? 'has-error' : ''}
                      minHeight="240px"
                    />
                    {errors.description_bn && <span className="pf-error">{errors.description_bn}</span>}
                  </div>
                </div>

                {/* English Content Panel */}
                <div style={{ display: descLang === 'en' ? 'flex' : 'none', flexDirection: 'column', gap: '16px' }}>
                  <div className="pf-field">
                    <label className="pf-label">
                      <span>Summary (English Overview)</span>
                      <span className="pf-label-badge">Short Overview</span>
                    </label>
                    <RichTextEditor
                      key={`summary-en-${product?.id || 'new'}-${editorKey}`}
                      value={formData.summary_en || product?.summary_en || ''}
                      onChange={(value) => handleRichTextChange('summary_en', value)}
                      placeholder="Brief product summary..."
                      minHeight="120px"
                    />
                    <span className="pf-hint">Quick summary displayed near top of product page</span>
                  </div>

                  <div className="pf-field">
                    <label className="pf-label">
                      <span>Detailed Description (English) <span className="req">*</span></span>
                    </label>
                    <RichTextEditor
                      key={`description-en-${product?.id || 'new'}-${editorKey}`}
                      value={formData.description_en || product?.description_en || ''}
                      onChange={(value) => handleRichTextChange('description_en', value)}
                      placeholder="Enter detailed product description and specifications..."
                      className={errors.description_en ? 'has-error' : ''}
                      minHeight="240px"
                    />
                    {errors.description_en && <span className="pf-error">{errors.description_en}</span>}
                  </div>
                </div>
              </div>
            </div>

            {/* Card 4: Product Attributes & Variations */}
            <div className="pf-card">
              <div className="pf-card-header">
                <h3 className="pf-card-title">
                  <Sliders size={17} /> Attributes & Variations (কালার, সাইজ ও অন্যান্য অপশন)
                </h3>
                <span className="pf-card-subtitle">Options with price adjustments (+৳)</span>
              </div>
              <div className="pf-card-body">
                <div className="attr-section">
                  <p className="attr-section-hint">
                    প্রোডাক্টের অপশন/ভ্যারিয়েন্ট তৈরি করুন। প্রতি অপশনে প্রয়োজনে অতিরিক্ত মূল্য (+৳) দিতে পারেন (যেমন: হিজাব ছাড়া ০৳, হিজাব সহ ৩০০৳)।
                  </p>

                  {/* Color */}
                  <div className="attr-group">
                    <label className="attr-toggle">
                      <input
                        type="checkbox"
                        checked={attributes.colors?.enabled ?? false}
                        onChange={() => toggleAttr('colors')}
                      />
                      <span className="attr-toggle-label">
                        <span className="attr-swatch" style={{ background: 'linear-gradient(135deg,#f87171,#60a5fa,#34d399)' }}></span>
                        Color (কালার)
                      </span>
                    </label>
                    {attributes.colors?.enabled && (
                      <div className="attr-options">
                        <div className="attr-placement-selector">
                          <span className="attr-placement-label">📍 পজিশন:</span>
                          <div className="attr-placement-pills">
                            <button
                              type="button"
                              className={`attr-placement-pill ${(!attributes.colors.placement || attributes.colors.placement === 'default') ? 'active' : ''}`}
                              onClick={() => updateAttrPlacement('colors', 'default')}
                              title="প্রোডাক্ট সামারির নিচে ও কার্ট বাটনের উপরে"
                            >
                              📦 ডিফল্ট
                            </button>
                            <button
                              type="button"
                              className={`attr-placement-pill below-price ${attributes.colors.placement === 'below_price' ? 'active' : ''}`}
                              onClick={() => updateAttrPlacement('colors', 'below_price')}
                              title="প্রাইসের ঠিক নিচে ডিসপ্লে হবে"
                            >
                              💰 প্রাইসের নিচে
                            </button>
                          </div>
                        </div>
                        <div className="attr-chips">
                          {normalizeOptions(attributes.colors.options).map(opt => (
                            <span key={opt.name} className="attr-chip color-chip">
                              {opt.name}
                              {opt.extra_price > 0 && (
                                <span className="attr-price-badge">+৳{opt.extra_price}</span>
                              )}
                              <button type="button" onClick={() => removeAttrOption('colors', opt.name)}>×</button>
                            </span>
                          ))}
                        </div>
                        <div className="attr-add-row">
                          <input
                            type="text"
                            className="attr-name-input"
                            value={attrInputs.colors.name}
                            onChange={e => setAttrInputs(prev => ({ ...prev, colors: { ...prev.colors, name: e.target.value } }))}
                            onKeyDown={e => handleAttrKeyDown('colors', e)}
                            placeholder="কালারের নাম (যেমন: পেঁয়াজ কালার, কালো)"
                          />
                          <input
                            type="number"
                            className="attr-price-input"
                            value={attrInputs.colors.extra_price}
                            onChange={e => setAttrInputs(prev => ({ ...prev, colors: { ...prev.colors, extra_price: e.target.value } }))}
                            onKeyDown={e => handleAttrKeyDown('colors', e)}
                            placeholder="+৳ Extra (0)"
                            min="0"
                          />
                          {parseFloat(attrInputs.colors.extra_price) > 0 && parseFloat(formData.discount_price || formData.price) > 0 && (
                            <span className="attr-price-preview">
                              মোট: ৳{(parseFloat(formData.discount_price || formData.price) + parseFloat(attrInputs.colors.extra_price))}
                            </span>
                          )}
                          <button type="button" className="attr-add-btn" onClick={() => addAttrOption('colors')}>+ Add</button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Size */}
                  <div className="attr-group">
                    <label className="attr-toggle">
                      <input
                        type="checkbox"
                        checked={attributes.sizes?.enabled ?? false}
                        onChange={() => toggleAttr('sizes')}
                      />
                      <span className="attr-toggle-label">
                        <span className="attr-icon">📐</span>
                        Size (সাইজ)
                      </span>
                    </label>
                    {attributes.sizes?.enabled && (
                      <div className="attr-options">
                        <div className="attr-placement-selector">
                          <span className="attr-placement-label">📍 পজিশন:</span>
                          <div className="attr-placement-pills">
                            <button
                              type="button"
                              className={`attr-placement-pill ${(!attributes.sizes.placement || attributes.sizes.placement === 'default') ? 'active' : ''}`}
                              onClick={() => updateAttrPlacement('sizes', 'default')}
                              title="প্রোডাক্ট সামারির নিচে ও কার্ট বাটনের উপরে"
                            >
                              📦 ডিফল্ট
                            </button>
                            <button
                              type="button"
                              className={`attr-placement-pill below-price ${attributes.sizes.placement === 'below_price' ? 'active' : ''}`}
                              onClick={() => updateAttrPlacement('sizes', 'below_price')}
                              title="প্রাইসের ঠিক নিচে ডিসপ্লে হবে"
                            >
                              💰 প্রাইসের নিচে
                            </button>
                          </div>
                        </div>
                        <div className="attr-chips">
                          {normalizeOptions(attributes.sizes.options).map(opt => (
                            <span key={opt.name} className="attr-chip size-chip">
                              {opt.name}
                              {opt.extra_price > 0 && (
                                <span className="attr-price-badge">+৳{opt.extra_price}</span>
                              )}
                              <button type="button" onClick={() => removeAttrOption('sizes', opt.name)}>×</button>
                            </span>
                          ))}
                        </div>
                        <div className="attr-add-row">
                          <input
                            type="text"
                            className="attr-name-input"
                            value={attrInputs.sizes.name}
                            onChange={e => setAttrInputs(prev => ({ ...prev, sizes: { ...prev.sizes, name: e.target.value } }))}
                            onKeyDown={e => handleAttrKeyDown('sizes', e)}
                            placeholder="সাইজ (যেমন: 50, 52, 54, M, L, XL)"
                          />
                          <input
                            type="number"
                            className="attr-price-input"
                            value={attrInputs.sizes.extra_price}
                            onChange={e => setAttrInputs(prev => ({ ...prev, sizes: { ...prev.sizes, extra_price: e.target.value } }))}
                            onKeyDown={e => handleAttrKeyDown('sizes', e)}
                            placeholder="+৳ Extra (0)"
                            min="0"
                          />
                          {parseFloat(attrInputs.sizes.extra_price) > 0 && parseFloat(formData.discount_price || formData.price) > 0 && (
                            <span className="attr-price-preview">
                              মোট: ৳{(parseFloat(formData.discount_price || formData.price) + parseFloat(attrInputs.sizes.extra_price))}
                            </span>
                          )}
                          <button type="button" className="attr-add-btn" onClick={() => addAttrOption('sizes')}>+ Add</button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Weight */}
                  <div className="attr-group">
                    <label className="attr-toggle">
                      <input
                        type="checkbox"
                        checked={attributes.weights?.enabled ?? false}
                        onChange={() => toggleAttr('weights')}
                      />
                      <span className="attr-toggle-label">
                        <span className="attr-icon">⚖️</span>
                        Weight (ওজন / পরিমাণ)
                      </span>
                    </label>
                    {attributes.weights?.enabled && (
                      <div className="attr-options">
                        <div className="attr-placement-selector">
                          <span className="attr-placement-label">📍 পজিশন:</span>
                          <div className="attr-placement-pills">
                            <button
                              type="button"
                              className={`attr-placement-pill ${(!attributes.weights.placement || attributes.weights.placement === 'default') ? 'active' : ''}`}
                              onClick={() => updateAttrPlacement('weights', 'default')}
                              title="প্রোডাক্ট সামারির নিচে ও কার্ট বাটনের উপরে"
                            >
                              📦 ডিফল্ট
                            </button>
                            <button
                              type="button"
                              className={`attr-placement-pill below-price ${attributes.weights.placement === 'below_price' ? 'active' : ''}`}
                              onClick={() => updateAttrPlacement('weights', 'below_price')}
                              title="প্রাইসের ঠিক নিচে ডিসপ্লে হবে"
                            >
                              💰 প্রাইসের নিচে
                            </button>
                          </div>
                        </div>
                        <div className="attr-chips">
                          {normalizeOptions(attributes.weights.options).map(opt => (
                            <span key={opt.name} className="attr-chip weight-chip">
                              {opt.name}
                              {opt.extra_price > 0 && (
                                <span className="attr-price-badge">+৳{opt.extra_price}</span>
                              )}
                              <button type="button" onClick={() => removeAttrOption('weights', opt.name)}>×</button>
                            </span>
                          ))}
                        </div>
                        <div className="attr-add-row">
                          <input
                            type="text"
                            className="attr-name-input"
                            value={attrInputs.weights.name}
                            onChange={e => setAttrInputs(prev => ({ ...prev, weights: { ...prev.weights, name: e.target.value } }))}
                            onKeyDown={e => handleAttrKeyDown('weights', e)}
                            placeholder="পরিমাণ (যেমন: 500gm, 1kg)"
                          />
                          <input
                            type="number"
                            className="attr-price-input"
                            value={attrInputs.weights.extra_price}
                            onChange={e => setAttrInputs(prev => ({ ...prev, weights: { ...prev.weights, extra_price: e.target.value } }))}
                            onKeyDown={e => handleAttrKeyDown('weights', e)}
                            placeholder="+৳ Extra (0)"
                            min="0"
                          />
                          {parseFloat(attrInputs.weights.extra_price) > 0 && parseFloat(formData.discount_price || formData.price) > 0 && (
                            <span className="attr-price-preview">
                              মোট: ৳{(parseFloat(formData.discount_price || formData.price) + parseFloat(attrInputs.weights.extra_price))}
                            </span>
                          )}
                          <button type="button" className="attr-add-btn" onClick={() => addAttrOption('weights')}>+ Add</button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Custom Attribute Groups */}
                  {(attributes.custom || []).map(customGroup => {
                    const cInputs = customAttrInputs[customGroup.id] || { name: '', extra_price: '' };
                    const extraVal = parseFloat(cInputs.extra_price) || 0;
                    const currentBase = parseFloat(formData.discount_price || formData.price) || 0;
                    const cOptions = normalizeOptions(customGroup.options);

                    return (
                      <div className="attr-group" key={customGroup.id}>
                        <div className="custom-attr-header">
                          <label className="attr-toggle">
                            <input
                              type="checkbox"
                              checked={customGroup.enabled}
                              onChange={() => toggleCustomGroup(customGroup.id)}
                            />
                            <span className="attr-toggle-label">
                              <span className="attr-icon">✨</span>
                              {customGroup.name}
                            </span>
                          </label>
                          <button
                            type="button"
                            className="custom-attr-del-btn"
                            onClick={() => removeCustomGroup(customGroup.id)}
                            title="Delete this attribute group"
                          >
                            🗑️ গ্রুপ মুছুন
                          </button>
                        </div>

                        {customGroup.enabled && (
                          <div className="attr-options">
                            <div className="attr-placement-selector">
                              <span className="attr-placement-label">📍 ডিসপ্লে পজিশন:</span>
                              <div className="attr-placement-pills">
                                <button
                                  type="button"
                                  className={`attr-placement-pill ${(!customGroup.placement || customGroup.placement === 'default') ? 'active' : ''}`}
                                  onClick={() => updateCustomGroupPlacement(customGroup.id, 'default')}
                                  title="প্রোডাক্ট সামারির নিচে ও কার্ট বাটনের উপরে"
                                >
                                  📦 ডিফল্ট পজিশন
                                </button>
                                <button
                                  type="button"
                                  className={`attr-placement-pill below-price ${customGroup.placement === 'below_price' ? 'active' : ''}`}
                                  onClick={() => updateCustomGroupPlacement(customGroup.id, 'below_price')}
                                  title="প্রোডাক্ট ডিটেইলস পেইজে প্রাইসের ঠিক নিচে ডিসপ্লে হবে"
                                >
                                  💰 প্রাইসের নিচে (Below Price)
                                </button>
                              </div>
                            </div>
                            <div className="attr-chips">
                              {cOptions.map(opt => (
                                <span key={opt.name} className="attr-chip custom-chip">
                                  {opt.name}
                                  {opt.extra_price > 0 && (
                                    <span className="attr-price-badge">+৳{opt.extra_price}</span>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => removeCustomOption(customGroup.id, opt.name)}
                                  >
                                    ×
                                  </button>
                                </span>
                              ))}
                            </div>
                            <div className="attr-add-row">
                              <input
                                type="text"
                                className="attr-name-input"
                                value={cInputs.name}
                                onChange={e => {
                                  const val = e.target.value;
                                  setCustomAttrInputs(prev => ({
                                    ...prev,
                                    [customGroup.id]: { ...(prev[customGroup.id] || { name: '', extra_price: '' }), name: val }
                                  }));
                                }}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') { e.preventDefault(); addCustomOption(customGroup.id); }
                                }}
                                placeholder={`অপশনের নাম (যেমন: ${customGroup.name.includes('হিজাব') ? 'হিজাব সহ / হিজাব ছাড়া' : customGroup.name + ' অপশন'})`}
                              />
                              <input
                                type="number"
                                className="attr-price-input"
                                value={cInputs.extra_price}
                                onChange={e => {
                                  const val = e.target.value;
                                  setCustomAttrInputs(prev => ({
                                    ...prev,
                                    [customGroup.id]: { ...(prev[customGroup.id] || { name: '', extra_price: '' }), extra_price: val }
                                  }));
                                }}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') { e.preventDefault(); addCustomOption(customGroup.id); }
                                }}
                                placeholder="+৳ Extra (0)"
                                min="0"
                              />
                              {extraVal > 0 && currentBase > 0 && (
                                <span className="attr-price-preview">মোট: ৳{currentBase + extraVal}</span>
                              )}
                              <button
                                type="button"
                                className="attr-add-btn"
                                onClick={() => addCustomOption(customGroup.id)}
                              >
                                + Add
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Add Custom Attribute Group Button */}
                  {showNewGroupInput ? (
                    <div className="new-group-inline">
                      <input
                        type="text"
                        placeholder="অ্যাট্রিবিউট গ্রুপের নাম (যেমন: হিজাব, ম্যাটেরিয়াল)"
                        value={newGroupName}
                        onChange={e => setNewGroupName(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') { e.preventDefault(); addCustomGroup(); }
                        }}
                        autoFocus
                      />
                      <button type="button" className="attr-add-btn" onClick={addCustomGroup}>
                        যোগ করুন
                      </button>
                      <button
                        type="button"
                        className="btn-cancel"
                        style={{ padding: '7px 12px', borderRadius: '6px', cursor: 'pointer', border: '1px solid #ccc' }}
                        onClick={() => { setShowNewGroupInput(false); setNewGroupName(''); }}
                      >
                        বাতিল
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="add-custom-group-btn"
                      onClick={() => setShowNewGroupInput(true)}
                    >
                      + Add Custom Attribute Group (যেমন: হিজাব, প্যাকেজিং)
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ========================================================
              RIGHT COLUMN (35% width) - Sidebar
             ======================================================== */}
          <div className="pf-col-side">
            {/* Card 1: Publishing & Visibility */}
            <div className="pf-card">
              <div className="pf-card-header">
                <h3 className="pf-card-title">
                  <CheckCircle2 size={17} color="#2563eb" /> Publishing & Visibility
                </h3>
              </div>
              <div className="pf-card-body">
                <div className="pf-toggle-list">
                  <div className="pf-toggle-item">
                    <div className="pf-toggle-info">
                      <span className="pf-toggle-title">Active Product</span>
                      <span className="pf-toggle-desc">Show publicly on website</span>
                    </div>
                    <label className="pf-switch">
                      <input
                        type="checkbox"
                        name="is_active"
                        checked={formData.is_active}
                        onChange={handleChange}
                      />
                      <span className="pf-slider"></span>
                    </label>
                  </div>

                  <div className="pf-toggle-item">
                    <div className="pf-toggle-info">
                      <span className="pf-toggle-title">Featured Product</span>
                      <span className="pf-toggle-desc">Highlight on homepage</span>
                    </div>
                    <label className="pf-switch">
                      <input
                        type="checkbox"
                        name="is_featured"
                        checked={formData.is_featured}
                        onChange={handleChange}
                      />
                      <span className="pf-slider"></span>
                    </label>
                  </div>

                  <div className="pf-toggle-item">
                    <div className="pf-toggle-info">
                      <span className="pf-toggle-title">Free Delivery</span>
                      <span className="pf-toggle-desc">Zero delivery fee badge</span>
                    </div>
                    <label className="pf-switch">
                      <input
                        type="checkbox"
                        name="free_delivery"
                        checked={formData.free_delivery}
                        onChange={handleChange}
                      />
                      <span className="pf-slider"></span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Card 2: Category & Organization */}
            <div className="pf-card">
              <div className="pf-card-header">
                <h3 className="pf-card-title">
                  <Tag size={17} color="#2563eb" /> Organization (ক্যাটাগরি)
                </h3>
              </div>
              <div className="pf-card-body">
                <div className="pf-field">
                  <label className="pf-label">
                    Category <span className="req">*</span>
                  </label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    className={`pf-select ${errors.category ? 'has-error' : ''}`}
                  >
                    <option value="">Select Category</option>
                    {Array.isArray(categories) && categories.map(cat => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name_en || cat.name_bn}
                      </option>
                    ))}
                  </select>
                  {errors.category && <span className="pf-error">{errors.category}</span>}
                </div>
              </div>
            </div>

            {/* Card 3: Pricing, Cost & Live Profit Engine */}
            <div className="pf-card">
              <div className="pf-card-header">
                <h3 className="pf-card-title">
                  <DollarSign size={17} color="#10b981" /> Pricing & Profit Engine
                </h3>
                <span className="pf-card-subtitle">ক্রয় ও বিক্রয় মূল্য (৳)</span>
              </div>
              <div className="pf-card-body">
                <div className="pf-pricing-grid">
                  <div className="pf-field">
                    <label className="pf-label">
                      <span>Buying Price (ক্রয় মূল্য ৳)</span>
                      <span className="pf-label-badge" style={{ background: '#ecfdf5', color: '#059669' }}>COGS / Cost</span>
                    </label>
                    <div className="pf-price-input-wrap">
                      <span className="pf-currency-symbol">৳</span>
                      <input
                        type="number"
                        name="buying_price"
                        value={formData.buying_price}
                        onChange={handleChange}
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        className="pf-input pf-input-currency"
                        style={{ borderColor: '#a7f3d0' }}
                      />
                    </div>
                    {errors.buying_price && <span className="pf-error">{errors.buying_price}</span>}
                  </div>

                  <div className="pf-field">
                    <label className="pf-label">
                      <span>Selling Price (বিক্রয় মূল্য ৳) <span className="req">*</span></span>
                    </label>
                    <div className="pf-price-input-wrap">
                      <span className="pf-currency-symbol">৳</span>
                      <input
                        type="number"
                        name="price"
                        value={formData.price}
                        onChange={handleChange}
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        className={`pf-input pf-input-currency ${errors.price ? 'has-error' : ''}`}
                        style={{ fontWeight: 600 }}
                      />
                    </div>
                    {errors.price && <span className="pf-error">{errors.price}</span>}
                  </div>

                  <div className="pf-field">
                    <label className="pf-label">
                      <span>Discount Price (অফার মূল্য ৳)</span>
                      <span className="pf-label-badge">Optional</span>
                    </label>
                    <div className="pf-price-input-wrap">
                      <span className="pf-currency-symbol">৳</span>
                      <input
                        type="number"
                        name="discount_price"
                        value={formData.discount_price}
                        onChange={handleChange}
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        className={`pf-input pf-input-currency ${errors.discount_price ? 'has-error' : ''}`}
                      />
                    </div>
                    {errors.discount_price && <span className="pf-error">{errors.discount_price}</span>}
                  </div>
                </div>

                {/* Live Profit Matrix */}
                {hasPricingData && (
                  <div className={`pf-profit-box ${isLoss ? 'is-loss' : ''}`}>
                    <div className="pf-profit-grid">
                      <div className="pf-profit-kpi">
                        <span className="pf-profit-kpi-label">💰 লাভ (Profit)</span>
                        <span className="pf-profit-kpi-val">৳{unitProfit.toFixed(2)}</span>
                      </div>
                      <div className="pf-profit-kpi">
                        <span className="pf-profit-kpi-label">📈 মার্জিন (Margin)</span>
                        <span className="pf-profit-kpi-val">{profitMargin}%</span>
                      </div>
                      <div className="pf-profit-kpi" style={{ gridColumn: 'span 2' }}>
                        <span className="pf-profit-kpi-label">🏷️ মার্কআপ (Markup on Cost)</span>
                        <span className="pf-profit-kpi-val" style={{ color: '#0284c7' }}>{markupPercentage}%</span>
                      </div>
                    </div>
                    {isLoss && (
                      <div className="pf-profit-loss-alert">
                        <AlertTriangle size={14} /> সাবধান: বিক্রয় মূল্য ক্রয় মূল্যের সমান বা কম! (Selling at Loss)
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Card 4: Inventory & Logistics */}
            <div className="pf-card">
              <div className="pf-card-header">
                <h3 className="pf-card-title">
                  <Package size={17} color="#3b82f6" /> Inventory & Logistics
                </h3>
                <span className="pf-card-subtitle">মজুদ ও কোড</span>
              </div>
              <div className="pf-card-body">
                <div className="pf-row-2">
                  <div className="pf-field">
                    <label className="pf-label">Stock <span className="req">*</span></label>
                    <input
                      type="number"
                      name="stock"
                      value={formData.stock}
                      onChange={handleChange}
                      min="0"
                      placeholder="0"
                      className={`pf-input ${errors.stock ? 'has-error' : ''}`}
                    />
                    {errors.stock && <span className="pf-error">{errors.stock}</span>}
                  </div>

                  <div className="pf-field">
                    <label className="pf-label">Low Alert</label>
                    <input
                      type="number"
                      name="low_stock_threshold"
                      value={formData.low_stock_threshold}
                      onChange={handleChange}
                      min="1"
                      placeholder="5"
                      className="pf-input"
                      title="কত পিসে নামলে অ্যালার্ট দিবে"
                    />
                  </div>
                </div>

                <div className="pf-row-2">
                  <div className="pf-field">
                    <label className="pf-label">SKU</label>
                    <input
                      type="text"
                      name="sku"
                      value={formData.sku}
                      onChange={handleChange}
                      placeholder="e.g. TSH-01"
                      className="pf-input"
                    />
                  </div>

                  <div className="pf-field">
                    <label className="pf-label">Weight (kg)</label>
                    <input
                      type="number"
                      name="weight_kg"
                      value={formData.weight_kg}
                      onChange={handleChange}
                      step="0.001"
                      min="0"
                      placeholder="e.g. 0.5"
                      className="pf-input"
                    />
                  </div>
                </div>

                <div className="pf-field">
                  <label className="pf-label">Barcode / EAN</label>
                  <input
                    type="text"
                    name="barcode"
                    value={formData.barcode}
                    onChange={handleChange}
                    placeholder="e.g. 890123456789"
                    className="pf-input"
                  />
                </div>
              </div>
            </div>

            {/* Card 5: Supplier & Procurement */}
            <div className="pf-card">
              <div className="pf-card-header">
                <h3 className="pf-card-title">
                  <Truck size={17} color="#8b5cf6" /> Supplier / Procurement
                </h3>
                <span className="pf-card-subtitle">মহাজন ও ক্রয়ের মেমো</span>
              </div>
              <div className="pf-card-body">
                <div className="pf-field">
                  <label className="pf-label">Supplier / Vendor Name</label>
                  <input
                    type="text"
                    name="supplier_name"
                    value={formData.supplier_name}
                    onChange={handleChange}
                    placeholder="কারখানা বা মহাজনের নাম"
                    className="pf-input"
                  />
                </div>

                <div className="pf-field">
                  <label className="pf-label">Contact / Phone</label>
                  <input
                    type="text"
                    name="supplier_contact"
                    value={formData.supplier_contact}
                    onChange={handleChange}
                    placeholder="মোবাইল বা যোগাযোগ"
                    className="pf-input"
                  />
                </div>

                <div className="pf-field">
                  <label className="pf-label">Memo / Invoice No</label>
                  <input
                    type="text"
                    name="supplier_memo_no"
                    value={formData.supplier_memo_no}
                    onChange={handleChange}
                    placeholder="ক্রয় রসিদ বা মেমো নম্বর"
                    className="pf-input"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Sticky Bottom Actions Bar */}
        <div className="pf-sticky-footer">
          <div className="pf-footer-summary">
            <span className={`pf-footer-status-pill ${formData.is_active ? 'active' : 'draft'}`}>
              {formData.is_active ? '● Live on Store' : '○ Draft Mode'}
            </span>
            {regularPriceNum > 0 && (
              <span style={{ fontSize: '0.82rem', color: '#1e293b' }}>
                Selling: <strong>৳{effectiveSellingPrice.toFixed(2)}</strong>
                {buyingPriceNum > 0 && (
                  <span style={{ color: unitProfit >= 0 ? '#16a34a' : '#ef4444', marginLeft: '6px' }}>
                    ({unitProfit >= 0 ? '+' : ''}৳{unitProfit.toFixed(2)} profit)
                  </span>
                )}
              </span>
            )}
          </div>
          <div className="pf-footer-actions">
            <button
              type="button"
              onClick={onClose}
              className="pf-btn-cancel"
              disabled={loading || imageUploading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="pf-btn-save"
              disabled={loading || imageUploading}
            >
              {loading ? 'Saving Product...' : imageUploading ? 'Uploading Images...' : 'Save Product'}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default ProductFormModal;
